import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, Menu, X, Heart, MapPin, Bell, LogOut, Settings, Package, ChevronDown, Plus, Store, } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore, useAuth } from '@/store/authStore';
import { useCart } from '@/store/cartStore';
import productsService, { Category } from '@/services/productsService';
import Button from '@/components/ui/Button';
import { UPLOADS_URL } from '@/utils/utils';
//import { cn } from '@/utils/utils';

const Header: React.FC = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const { logout } = useAuthStore();
    const { itemCount, fetchCartCount } = useCart();

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [categories, setCategories] = useState<Category[]>([]);

    // Cargar contador del carrito al montar el componente
    useEffect(() => {
        if (isAuthenticated) {
            fetchCartCount();
        }
    //}, [isAuthenticated, fetchCartCount]);
    }, []);

    // Cargar categorías para el menú
    useEffect(() => {
        const loadCategories = async () => {
            try {
                const data = await productsService.getCategories(false, true); // Solo activas, formato plano
                setCategories(data.slice(0, 8)); // Solo las primeras 8 para el header
            } catch (error) {
                console.error('Error loading categories for header:', error);
            }
        };
        loadCategories();
    }, []);

    // Mock data para wishlist - implementar cuando tengamos el endpoint
    const wishlistCount = 0;

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
            setSearchQuery('');
        }
    };

    const handleLogout = () => {
        logout();
        setIsUserMenuOpen(false);
        navigate('/');
    };

    return (
        <header className="bg-white shadow-sm sticky top-0 z-50">
            {/* Top bar */}
            <div className="bg-primary-600 text-white text-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-2">
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1">
                                <MapPin className="w-4 h-4" />
                                <span>Envío gratis a Lima</span>
                            </div>
                        </div>
                        <div className="hidden md:flex items-center space-x-4">
                            <Link to="/help" className="hover:text-primary-200">
                                Centro de ayuda
                            </Link>
                            <Link to="/track" className="hover:text-primary-200">
                                Rastrear pedido
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main header */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary-600 rounded-xl flex items-center justify-center">
                            <span className="text-white font-bold text-lg sm:text-xl">T</span>
                        </div>
                        <span className="hidden sm:block text-xl sm:text-2xl font-bold text-gray-900">
                            Eisgemi
                        </span>
                    </Link>

                    {/* Search bar - Hidden on mobile, shown in mobile menu */}
                    <div className="hidden md:flex flex-1 max-w-2xl mx-4">
                        <form onSubmit={handleSearch} className="relative w-full">
                            <input
                                type="text"
                                placeholder="Buscar productos..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-4 pr-12 py-2.5 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                            />
                            <button
                                type="submit"
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary-600 text-white p-2 rounded-full hover:bg-primary-700 transition-colors"
                            >
                                <Search className="w-4 h-4" />
                            </button>
                        </form>
                    </div>

                    {/* Right section */}
                    <div className="flex items-center space-x-2 sm:space-x-4">
                        {/* Wishlist - Hidden on small mobile */}
                        <Link
                            to="/wishlist"
                            className="hidden xs:block relative p-2 text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <Heart className="w-5 h-5 sm:w-6 sm:h-6" />
                            {wishlistCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center text-[10px] sm:text-xs">
                                    {wishlistCount > 99 ? '99+' : wishlistCount}
                                </span>
                            )}
                        </Link>

                        {/* Cart */}
                        <Link
                            to="/cart"
                            className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6" />
                            {itemCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-primary-500 text-white text-xs rounded-full h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center text-[10px] sm:text-xs">
                                    {itemCount > 99 ? '99+' : itemCount}
                                </span>
                            )}
                        </Link>

                        {/* User menu */}
                        {isAuthenticated ? (
                            <div className="relative">
                                <button
                                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                    className="flex items-center space-x-1 sm:space-x-2 p-1 sm:p-2 text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-primary-100 rounded-full flex items-center justify-center">
                                        {user?.profile_image ? (
                                            <img
                                                src={UPLOADS_URL + user.profile_image}
                                                alt={user.first_name}
                                                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover"
                                            />
                                        ) : (
                                            <User className="w-3 h-3 sm:w-5 sm:h-5 text-primary-600" />
                                        )}
                                    </div>
                                    <span className="hidden lg:block font-medium text-sm">
                                        {user?.first_name}
                                    </span>
                                    <ChevronDown className="hidden sm:block w-4 h-4" />
                                </button>
                                {/* <Link
                                    to="/sell"
                                    className="hidden sm:flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                                >
                                    <Plus className="w-4 h-4" />
                                    <span>Vender</span>
                                </Link> */}
                                {/* User dropdown */}
                                <AnimatePresence>
                                    {isUserMenuOpen && isAuthenticated && (
                                        <>
                                            {/* Overlay */}
                                            <div
                                                className="fixed inset-0 z-[60] bg-black bg-opacity-25"
                                                onClick={() => setIsUserMenuOpen(false)}
                                            />

                                            {/* Dropdown Menu */}
                                            <motion.div
                                                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                                transition={{ duration: 0.15 }}
                                                className="fixed top-[120px] right-4 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-[70] max-h-96 overflow-y-auto"
                                            >
                                                {/* User Info Header */}
                                                <div className="px-4 py-4 border-b border-gray-100 bg-gray-50 rounded-t-lg">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 relative">
                                                            {user?.profile_image ? (
                                                                <img
                                                                    src={user.profile_image}
                                                                    alt={user.first_name}
                                                                    className="w-12 h-12 rounded-full object-cover"
                                                                />
                                                            ) : (
                                                                <User className="w-7 h-7 text-primary-600" />
                                                            )}
                                                            {/* Badge de vendedor */}
                                                            {user?.user_type === 'seller' && (
                                                                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                                                    <Store className="w-3 h-3 text-white" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center space-x-2">
                                                                <p className="font-semibold text-gray-900 text-sm truncate">
                                                                    {user?.first_name} {user?.last_name}
                                                                </p>
                                                                {user?.user_type === 'seller' && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                        Vendedor
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                                                        </div>
                                                        {/* Link para admin - solo visible si es admin */}
                                                        {user?.user_type === 'admin' && (
                                                            <Link
                                                                to="/admin/dashboard"
                                                                className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors"
                                                                onClick={() => setIsUserMenuOpen(false)}
                                                            >
                                                                <Settings className="w-5 h-5 text-gray-400" />
                                                                <span className="text-sm font-medium">Panel de Admin</span>
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Menu Items */}
                                                <div className="py-2">
                                                    <Link
                                                        to="/profile"
                                                        className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                    >
                                                        <Settings className="w-5 h-5 text-gray-400" />
                                                        <span className="text-sm font-medium">Mi perfil</span>
                                                    </Link>

                                                    <Link
                                                        to="/orders"
                                                        className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                    >
                                                        <Package className="w-5 h-5 text-gray-400" />
                                                        <span className="text-sm font-medium">Mis pedidos</span>
                                                    </Link>

                                                    <Link
                                                        to="/wishlist"
                                                        className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                    >
                                                        <Heart className="w-5 h-5 text-gray-400" />
                                                        <span className="text-sm font-medium">Lista de deseos</span>
                                                    </Link>

                                                    <Link
                                                        to="/notifications"
                                                        className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                    >
                                                        <Bell className="w-5 h-5 text-gray-400" />
                                                        <span className="text-sm font-medium">Notificaciones</span>
                                                    </Link>
                                                    <Link
                                                        to={user?.user_type === 'seller' ? '/seller/dashboard' : '/seller/apply'}
                                                        className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                    >
                                                        <Plus className="w-5 h-5 text-gray-400" />
                                                        <span className="text-sm font-medium">
                                                            {user?.user_type === 'seller' ? 'Panel de Vendedor' : 'Ser Vendedor'}
                                                        </span>
                                                    </Link>

                                                    {/* Divider */}
                                                    <div className="border-t border-gray-100 my-2"></div>

                                                    <button
                                                        onClick={() => {
                                                            handleLogout();
                                                            setIsUserMenuOpen(false);
                                                        }}
                                                        className="flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                                                    >
                                                        <LogOut className="w-5 h-5" />
                                                        <span className="text-sm font-medium">Cerrar sesión</span>
                                                    </button>
                                                </div>
                                            </motion.div>
                                        </>
                                    )}
                                </AnimatePresence>
                            </div>
                        ) : (
                            <div className="flex items-center space-x-1 sm:space-x-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate('/login')}
                                    className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                                >
                                    <span className="hidden sm:inline">Iniciar sesión</span>
                                    <span className="sm:hidden">Login</span>
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => navigate('/register')}
                                    className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                                >
                                    <span className="hidden sm:inline">Registrarse</span>
                                    <span className="sm:hidden">Registro</span>
                                </Button>
                            </div>
                        )}

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="md:hidden p-2 text-gray-600 hover:text-gray-900"
                        >
                            {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Categories bar */}
            <div className="border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="hidden md:flex items-center space-x-8 py-3">
                        {categories.map((category) => (
                            <Link
                                key={category.id}
                                to={`/category/${category.id}`}
                                className="text-gray-600 hover:text-primary-600 transition-colors text-sm font-medium"
                            >
                                {category.name}
                            </Link>
                        ))}
                        {categories.length > 0 && (
                            <Link
                                to="/categories"
                                className="text-primary-600 hover:text-primary-700 transition-colors text-sm font-medium"
                            >
                                Ver todas
                            </Link>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="md:hidden bg-white border-t border-gray-200 shadow-lg"
                    >
                        <div className="px-4 py-6 space-y-6 max-h-[calc(100vh-120px)] overflow-y-auto">
                            {/* Mobile Search */}
                            <div className="md:hidden">
                                <form onSubmit={handleSearch} className="relative">
                                    <input
                                        type="text"
                                        placeholder="Buscar productos..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    />
                                    <button
                                        type="submit"
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary-600 text-white p-2 rounded-full hover:bg-primary-700 transition-colors"
                                    >
                                        <Search className="w-5 h-5" />
                                    </button>
                                </form>
                            </div>

                            {/* Quick actions for mobile */}
                            <div className="grid grid-cols-2 gap-3">
                                <Link
                                    to="/wishlist"
                                    className="flex items-center justify-center space-x-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    <Heart className="w-5 h-5 text-gray-600" />
                                    <span className="text-sm font-medium text-gray-700">Favoritos</span>
                                    {wishlistCount > 0 && (
                                        <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                            {wishlistCount}
                                        </span>
                                    )}
                                </Link>

                                <Link
                                    to="/cart"
                                    className="flex items-center justify-center space-x-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    <ShoppingCart className="w-5 h-5 text-gray-600" />
                                    <span className="text-sm font-medium text-gray-700">Carrito</span>
                                    {itemCount > 0 && (
                                        <span className="bg-primary-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                            {itemCount}
                                        </span>
                                    )}
                                </Link>
                            </div>

                            {/* Categories */}
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-3 text-base">Categorías</h3>
                                <div className="grid grid-cols-2 gap-2">
                                    {categories.map((category) => (
                                        <Link
                                            key={category.id}
                                            to={`/category/${category.id}`}
                                            className="block p-3 bg-gray-50 rounded-lg hover:bg-primary-50 hover:text-primary-600 transition-colors text-sm"
                                            onClick={() => setIsMenuOpen(false)}
                                        >
                                            {category.name}
                                        </Link>
                                    ))}
                                    <Link
                                        to="/categories"
                                        className="col-span-2 text-center p-3 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 font-medium text-sm"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Ver todas las categorías
                                    </Link>
                                </div>
                            </div>

                            {/* User actions for mobile */}
                            {!isAuthenticated && (
                                <div className="space-y-3 pt-4 border-t border-gray-200">
                                    <Button
                                        variant="outline"
                                        fullWidth
                                        onClick={() => {
                                            navigate('/login');
                                            setIsMenuOpen(false);
                                        }}
                                    >
                                        Iniciar sesión
                                    </Button>
                                    <Button
                                        fullWidth
                                        onClick={() => {
                                            navigate('/register');
                                            setIsMenuOpen(false);
                                        }}
                                    >
                                        Registrarse
                                    </Button>
                                </div>
                            )}

                            {/* User menu items for mobile */}
                            {isAuthenticated && (
                                <div className="space-y-2 pt-4 border-t border-gray-200">
                                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                                            {user?.profile_image ? (
                                                <img
                                                    src={user.profile_image}
                                                    alt={user.first_name}
                                                    className="w-10 h-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <User className="w-6 h-6 text-primary-600" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900 text-sm">
                                                {user?.first_name} {user?.last_name}
                                            </p>
                                            <p className="text-xs text-gray-500">{user?.email}</p>
                                        </div>
                                    </div>

                                    <Link
                                        to="/profile"
                                        className="flex items-center space-x-3 p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <Settings className="w-5 h-5" />
                                        <span className="text-sm">Mi perfil</span>
                                    </Link>

                                    <Link
                                        to="/orders"
                                        className="flex items-center space-x-3 p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <Package className="w-5 h-5" />
                                        <span className="text-sm">Mis pedidos</span>
                                    </Link>

                                    <Link
                                        to="/notifications"
                                        className="flex items-center space-x-3 p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <Bell className="w-5 h-5" />
                                        <span className="text-sm">Notificaciones</span>
                                    </Link>
                                    <Link
                                        to={user?.user_type === 'seller' ? '/seller/dashboard' : '/seller/apply'}
                                        className="flex items-center space-x-3 p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <Plus className="w-5 h-5" />
                                        <span className="text-sm">
                                            {user?.user_type === 'seller' ? 'Panel de Vendedor' : 'Ser Vendedor'}
                                        </span>
                                    </Link>
                                    {user?.user_type === 'admin' && (
                                        <Link
                                            to="/admin/dashboard"
                                            className="flex items-center space-x-3 p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                            onClick={() => setIsMenuOpen(false)}
                                        >
                                            <Settings className="w-5 h-5" />
                                            <span className="text-sm">Panel de Admin</span>
                                        </Link>
                                    )}
                                    <button
                                        onClick={() => {
                                            handleLogout();
                                            setIsMenuOpen(false);
                                        }}
                                        className="flex items-center space-x-3 p-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors w-full text-left"
                                    >
                                        <LogOut className="w-5 h-5" />
                                        <span className="text-sm">Cerrar sesión</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Overlay for user menu */}
            {/* {isUserMenuOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsUserMenuOpen(false)}
                />
            )} */}
        </header>
    );
};

export default Header;